import fp from 'lodash/fp';
import { subscriptionStatus, accountantsIndustryName } from '../constants';
import { isBusinessAdmin, isBusinessUser } from '.';

export const notCanceled = (subscription) => (
  subscription.status !== subscriptionStatus.canceled ? subscription : {}
);

const commonSubscriptionIds = [
  'SmallTeam',
  'Individual'
];

const freeTrialDaysMap = {
  Individual: 14,
  SmallTeam: 14,
  BASIndividual: 7,
  BASSmallTeam: 7,
  Default: 14,
}

const findPlan = (planId) => fp.find(({ id }) => id === planId);

export const isCustomSubscription = (subscription) => fp.flow(
  fp.get('planId'),
  (planId) => !fp.contains(planId)(commonSubscriptionIds)
)(subscription);

export const isIndividualPlan = (plan) => plan.name.toLowerCase().includes('individual');

export const isOrganizationPlan = (plan) => plan.name.toLowerCase().includes('organisations');

/**
 * Determine the individual plan ID for users based on their industry.
 * Accountants and Bookkeepers industry will use a different price plan.
 * @param {string object} plansInUse active plan ids.
 * @param {string} userIndustryName user's industry name.
 * @returns individual plan id for user.
 */
export const getIndividualPlanIdByUserIndustry  = (plansInUse, userIndustryName) => {
  if (userIndustryName === accountantsIndustryName) {
    return plansInUse.BASIndividual;
  } else {
    return plansInUse.Individual;
  }
}

/**
 * Determine the small team (organisation) plan ID for users based on their industry.
 * Accountants and Bookkeepers industry will use a different price plan.
 * @param {string object} plansInUse active plan ids.
 * @param {string} userIndustryName user's industry name.
 * @returns small team plan id for user.
 */
export const getSmallTeamPlanIdByUserIndustry  = (plansInUse, userIndustryName) => {
  if (userIndustryName === accountantsIndustryName) {
    return plansInUse.BASSmallTeam;
  } else {
    return plansInUse.SmallTeam;
  }
}

/**
 * Determine user subscription plan based on their industry.
 * Used when user haven't got a valid subscription in caddie database.
 * After user have a valid subscription, we can just got the plan details from their subscription.
 * Accountants and Bookkeepers industry will use a different price plan.
 * @param {string object} plansInUse active plan ids.
 * @param {plan object array} plans all price plans in stripe.
 * @param {user object} user current user.
 * @param {string} userIndustryName current user's industry name.
 * @returns stripe plan.
 */
export const getPlanByUserIndustry = (plansInUse, plans, user, userIndustryName) => {
  // BAS Agents industry will have its own plan
  if (userIndustryName === accountantsIndustryName) {
    return isBusinessAdmin(user) ?
      findPlan(plansInUse.BASSmallTeam)(plans) :
      isBusinessUser(user) ?
        null :
        findPlan(plansInUse.BASIndividual)(plans);
  } else { // All other industries are under default plan.
    return isBusinessAdmin(user) ?
      findPlan(plansInUse.SmallTeam)(plans) :
      isBusinessUser(user) ?
        null :
        findPlan(plansInUse.Individual)(plans);
  }
};

/**
 * Get plan's free trial days.
 * @param {string object} plansInUse 
 * @param {string} planId 
 * @returns free trail days.
 */
export const getPlanFreeTrialDaysByPlanId = (plansInUse, planId) => {
  const planKey = fp.flow(
    fp.keys,
    fp.find((key) => plansInUse[key] === planId)
  )(plansInUse) || "Default";

  return freeTrialDaysMap[planKey];
};

/**
 * Get discounted price based on original price and coupon.
 * @param {Stripe object} coupon stripe coupon. 
 * @param {float} price original price.
 * @returns discounted price.
 */
export const getDiscountedPrice = (coupon, price) => {
  if (coupon === undefined || !coupon.valid) {
    return price;
  }

  if (coupon.percent_off !== undefined) {
    return price * (1 - (coupon.percent_off/100));
  }

  if (coupon.amount_off !== undefined) {
    return price - coupon.amount_off;
  }

  return price;
};



// WEBPACK FOOTER //
// ./src/services/subscription.js